import { startCase } from 'lodash-es';

/** Field id for the Job Level row (sourced from top-level profile.job_level). */
export const JOB_LEVEL_FIELD_ID = 'Job_Level';

/** Field id for active employees per profile (from profile.employee_count). */
export const EMPLOYEE_COUNT_FIELD_ID = '__Employee_Count';

// Preferred row order for job profile fields (field ids). May or may not include all fields.
export const FIELD_ROW_ORDER = [
  JOB_LEVEL_FIELD_ID,
  EMPLOYEE_COUNT_FIELD_ID,
  'Job_Profile_Summary',
  'Job_Description',
  'Additional_Job_Description',
  'Skill_Data',
  'Job_Classification_Data',
  'Responsibility_Data',
  'Training_Data',
  'Certification_Data',
  'Competency_Data',
  'Education_Data',
  'Language_Data',
  'Work_Experience_Data',
];

/**
 * Sorts profile column order by Job_Level (A–Z), then by title.
 * - If a profile has no Job_Level, it goes after those that do.
 * - If no profiles have Job_Level at all, we just sort by title.
 * @param {string[]} profileIds
 * @param {object[]} list - Profile objects with ulid, title, job_level
 * @returns {string[]}
 */
export const sortProfileIdsByJobLevel = (profileIds, list) => {
  const byUlid = Object.fromEntries(list.map((listProfile) => [listProfile.ulid, listProfile]));

  const getLevel = (ulid) => {
    const value = byUlid[ulid]?.job_level;

    return value || '';
  };

  const atLeastOneHasLevel = profileIds.some((id) => getLevel(id));

  if (!atLeastOneHasLevel) {
    return [...profileIds].sort((idA, idB) => {
      const titleA = byUlid[idA]?.title || '';
      const titleB = byUlid[idB]?.title || '';

      return titleA.localeCompare(titleB);
    });
  }

  return [...profileIds].sort((idA, idB) => {
    const levelA = getLevel(idA);
    const levelB = getLevel(idB);

    if (!levelA && !levelB) {
      const titleA = byUlid[idA]?.title || '';
      const titleB = byUlid[idB]?.title || '';

      return titleA.localeCompare(titleB);
    }

    if (!levelA) return 1;
    if (!levelB) return -1;

    const levelCompare = levelA.localeCompare(levelB);

    if (levelCompare !== 0) return levelCompare;

    const titleA = byUlid[idA]?.title || '';
    const titleB = byUlid[idB]?.title || '';

    return titleA.localeCompare(titleB);
  });
};

/**
 * Builds the union of all field ids across profiles (fields only), plus Job Level and Employee count rows, sorted by FIELD_ROW_ORDER then label.
 * @param {object[]} profilesList
 * @returns {{ id: string, label: string }[]}
 */
export const buildAllFieldsFromProfiles = (profilesList) => {
  const fieldsById = new Map();

  (profilesList || []).forEach((profile) => {
    const profileFields = profile.fields || profile.fields_json || {};

    Object.entries(profileFields).forEach(([fieldId, fieldMeta]) => {
      if (!fieldsById.has(fieldId)) {
        fieldsById.set(fieldId, { id: fieldId, label: startCase(fieldMeta?.label || fieldId) });
      }
    });
  });

  if (!fieldsById.has(JOB_LEVEL_FIELD_ID)) {
    fieldsById.set(JOB_LEVEL_FIELD_ID, { id: JOB_LEVEL_FIELD_ID, label: 'Job Level' });
  }

  if (!fieldsById.has(EMPLOYEE_COUNT_FIELD_ID)) {
    fieldsById.set(EMPLOYEE_COUNT_FIELD_ID, { id: EMPLOYEE_COUNT_FIELD_ID, label: 'Employee count' });
  }

  const fieldsList = Array.from(fieldsById.values());

  const orderIndex = (fieldId) => {
    const index = FIELD_ROW_ORDER.indexOf(fieldId);

    return index === -1 ? FIELD_ROW_ORDER.length : index;
  };

  return fieldsList.sort((fieldA, fieldB) => {
    const orderIndexA = orderIndex(fieldA.id);
    const orderIndexB = orderIndex(fieldB.id);

    if (orderIndexA !== orderIndexB) return orderIndexA - orderIndexB;
    return (fieldA.label || '').localeCompare(fieldB.label || '');
  });
};

/**
 * Builds table rows: one row per field, each column (profile ulid) holds that profile's value.
 * @param {{ id: string, label: string }[]} allFieldsList
 * @param {object[]} profilesList
 * @returns {object[]} Row objects with fieldId, fieldLabel, and [profileUlid]: value
 */
export const buildTableRowsFromFieldsAndProfiles = (allFieldsList, profilesList) => (allFieldsList || [])
  .map((field) => {
    const row = { fieldId: field.id, fieldLabel: field.label || field.id };

    (profilesList || []).forEach((profile) => {
      if (field.id === JOB_LEVEL_FIELD_ID) {
        const raw = profile.job_level;

        row[profile.ulid] = raw || '';

        return;
      }

      if (field.id === EMPLOYEE_COUNT_FIELD_ID) {
        const raw = profile.employee_count;

        row[profile.ulid] = raw || 0;

        return;
      }

      const profileFields = profile.fields || profile.fields_json || {};
      const cellEntry = profileFields[field.id];

      let value = '';

      if (cellEntry && typeof cellEntry === 'object' && 'value' in cellEntry) {
        value = cellEntry.value;
      } else if (typeof cellEntry === 'string') {
        value = cellEntry;
      }

      row[profile.ulid] = value ?? '';
    });

    return row;
  });

export const isEmpty = (value) => {
  if (value == null) return true;
  if (typeof value === 'string') return value.trim() === '';

  return false;
};

/**
 * Converts HTML to plain text. When content uses <li> list items, renders each as a bullet line
 * (• item) separated by newlines for display with white-space: pre-line.
 * @param {*} value
 * @returns {string}
 */
export const toPlainText = (value) => {
  if (!value) return value;

  const stringValue = typeof value === 'string' ? value : String(value);
  const trimmed = stringValue.trim();

  if (!trimmed) return trimmed;
  if (!trimmed.includes('<')) return trimmed;

  const listItemRegex = /<li[^>]*>([\s\S]*?)<\/li>/gi;
  const listItems = [];
  let listItemMatch = listItemRegex.exec(trimmed);

  while (listItemMatch !== null) {
    const itemText = listItemMatch[1].replace(/<[^>]+>/g, '').trim();

    if (itemText) listItems.push(itemText);

    listItemMatch = listItemRegex.exec(trimmed);
  }

  if (listItems.length > 0) {
    return listItems.map((item) => `• ${item}`).join('\n');
  }

  return trimmed.replace(/<[^>]+>/g, '').trim();
};

export const formatCellValue = (value) => {
  if (value == null || value === '') return '—';
  if (typeof value === 'string' && value.trim() === '') return '—';
  return value;
};

/**
 * Builds 2D array for CSV export: [headerRow, ...dataRows].
 * @param {{ id: string, header: string }[]} headerColumnList
 * @param {object[]} orderedRowsData - Row objects with fieldLabel and [col.id]: value
 * @param {(value: *) => string} toPlainTextFn
 * @returns {string[][]}
 */
export const buildCsvRowsForComparisonTable = (headerColumnList, orderedRowsData, toPlainTextFn) => {
  if (!headerColumnList?.length) return [];

  const headerRow = headerColumnList.map((col) => col.header);
  const dataRows = (orderedRowsData || []).map((row) => headerColumnList.map((col) => {
    const raw = col.id === 'fieldLabel' ? row.fieldLabel : row[col.id];

    return toPlainTextFn(raw);
  }));

  return [headerRow, ...dataRows];
};

// --- Graph / tree helpers (used by useArchitectureGraph and JobArchitectureCanvas)

export const sumEmployeeCountsForProfiles = (profiles) => (profiles || []).reduce(
  (sum, profile) => sum + (profile?.employee_count || 0),
  0,
);

export const getGroupTotalProfileCount = (group) => {
  const families = group?.families || [];

  return families.reduce((sum, family) => sum + (family?.profiles?.length || 0), 0);
};

export const getGroupArchitectureStats = (group) => {
  const families = group?.families || [];
  const familyCount = families.length;
  const employeeCount = families.reduce(
    (total, family) => total + sumEmployeeCountsForProfiles(family?.profiles),
    0,
  );

  return { familyCount, employeeCount };
};

export const getFamilyArchitectureStats = (family) => {
  const profiles = family?.profiles || [];

  return {
    profileCount: profiles.length,
    employeeCount: sumEmployeeCountsForProfiles(profiles),
  };
};

export const formatArchitectureFamilyCountLabel = (count) => (
  count === 1 ? '1 family' : `${count} families`
);

export const formatArchitectureProfilesCountLabel = (count) => (
  count === 1 ? '1 profile' : `${count} profiles`
);

export const formatArchitectureEmployeeCountLabel = (count) => (
  `${count} ${count === 1 ? 'employee' : 'employees'}`
);

export const formatArchitectureGroupStatsLine = (group) => {
  const { familyCount, employeeCount } = getGroupArchitectureStats(group);

  return `${formatArchitectureFamilyCountLabel(familyCount)} - ${formatArchitectureEmployeeCountLabel(employeeCount)}`;
};

export const formatArchitectureFamilyStatsLine = (family) => {
  const { profileCount, employeeCount } = getFamilyArchitectureStats(family);
  const profilePart = `${profileCount} job profile${profileCount === 1 ? '' : 's'}`;

  return `${profilePart} - ${formatArchitectureEmployeeCountLabel(employeeCount)}`;
};

export const groupId = (group) => `group-${group?.ulid || 'uncategorized'}`;

export const familyNodeId = (group, family) => {
  const groupUlid = group?.ulid || 'uncategorized';
  const familyUlid = family?.ulid || 'uncategorized';

  return `family-${groupUlid}-${familyUlid}`;
};

/**
 * Groups to render: all groups, or only those with at least one family when hideEmpty is true.
 * @param {object[]} groups
 * @param {boolean} hideEmpty
 * @returns {object[]}
 */
export const getGroupsToRender = (groups, hideEmpty) => {
  if (!groups?.length) return [];
  if (!hideEmpty) return groups;

  return groups.filter((group) => (group.families?.length || 0) > 0);
};

/**
 * Finds a family in the tree by ulid.
 * @param {object} tree - Tree with groups[].families[]
 * @param {string} ulid
 * @returns {object|null}
 */
export const findFamilyByUlid = (tree, ulid) => {
  const { groups } = tree || {};

  if (!groups || ulid == null || ulid === '') return null;

  for (let index = 0; index < groups.length; index += 1) {
    const group = groups[index];
    const family = group.families?.find((candidateFamily) => String(candidateFamily.ulid) === String(ulid));

    if (family) return family;
  }

  return null;
};
